import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import * as Types from '../types';
import axios from 'axios';

interface UserDataContextType {
    userData: any;
    isLoading: boolean;
    cartData: Types.CartData[];
    refreshUserData: () => Promise<void>;
    refreshCartData: () => Promise<void>;
    logout: () => void;
}

const UserDataContext = createContext<UserDataContextType | null>(null);

export const UserDataProvider = ({ children }: any) => {
    const [token, setToken] = useState(localStorage.getItem('authToken'));
    const [userData, setUserData] = useState([]);
    const [cartData, setCartData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchUserData = useCallback(async () => {
        if (!token) return;

        try {
          const response = await axios.get(`${import.meta.env.VITE_API_URL}/user`, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
              },
          });
          setUserData(response.data);
        } catch (error) {
            console.error('Error fetching user data:', error);
            localStorage.removeItem('authToken');
            setToken(null);
            setUserData([]);
        }
    }, [token]);

    const fetchCartData = useCallback(async () => {
        if (!token) { 
            const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
            setCartData(existingCart);
            return;
        };

        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/user/cart`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token,
                },
            });
            setCartData(response.data);
        } catch (error) {
            console.error('Error fetching cart data:', error);
            setCartData([]);
        }
    }, [token]);

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        await Promise.all([fetchUserData()]);
        setIsLoading(false);
    }, [fetchUserData]);

    useEffect(() => {
        fetchCartData();
        if (token) fetchAllData();
    }, [fetchAllData, token]);

    const logout = useCallback(() => {
        localStorage.removeItem('authToken');
        setToken(null);
        setUserData([]);
        setCartData([]);
        window.location.replace("/webapp");
    }, []);

    return (
        <UserDataContext.Provider
            value={{
              userData: userData,
              isLoading: isLoading,
              cartData: cartData,
              refreshUserData: fetchUserData,
              refreshCartData: fetchCartData,
              logout,
            }}
        >
            {children}
        </UserDataContext.Provider>
    );
};

export default function useUserData() {
    const context = useContext(UserDataContext);
    if (!context) {
        throw new Error('useUserData must be used within a UserDataProvider');
    }
    return context;
}